export const HANDOFF_BEAT_IDS = {
  parkedGoal: "parked_goal",
  territory: "territory",
  firstBrief: "first_brief",
} as const;

export type HandoffBeatId =
  (typeof HANDOFF_BEAT_IDS)[keyof typeof HANDOFF_BEAT_IDS];

export const HANDOFF_ANCHOR_ATTR = "data-handoff-anchor";

export const HANDOFF_READY_TIMEOUT_MS = 1500;

/**
 * `resolveHandoffBeats` 等 Pending 段数据落定的**硬上限**。
 *
 * 1.5s 的 `HANDOFF_READY_TIMEOUT_MS` 只是「数据已落定、再给布局一帧」的宽限，
 * 不能用来等网络 —— 冷启动的 Plan home 常常慢于它（见
 * `probeParkedGoalSourceSettled` 的注释）。段一直不落定（离线 / 不在 My goals）
 * 时由本上限收口，避免蒙层无限期挂着。
 */
export const HANDOFF_SOURCE_SETTLE_CAP_MS = 6000;

/** Tour 变体：已激活(贺词) / 未激活(安抚) */
export type HandoffVariant = "activated" | "unactivated";

/** Paywall 结果分支：A1 付费+完成设goal / A2 付费+中途退出 / B 未付费 */
export type HandoffBranch = "A1" | "A2" | "B";

export type HandoffSessionState = {
  active: boolean;
  beats: HandoffBeatId[];
  beatIndex: number;
  hasGoal: boolean;
  reducedMotion: boolean;
  /** Beats list resolved after probing Plan DOM */
  beatsResolved: boolean;
  variant: HandoffVariant;
  branch: HandoffBranch;
};

export type AnchorRect = {
  top: number;
  left: number;
  width: number;
  height: number;
  ready: boolean;
};

/**
 * parked goal 锚点探测是否该放弃（→ 塌成单拍 tour）。
 *
 * 两段式，**落定与否决定用哪条线**：
 * 1. 已落定 → 只看 `HANDOFF_READY_TIMEOUT_MS` 的布局宽限（从落定那刻起算）。卡都渲染完
 *    还没挂上锚点，就是真没有 pending goal；
 * 2. 未落定 → 看 `HANDOFF_SOURCE_SETTLE_CAP_MS` 硬收口，兜住离线 / 根本不在 My goals。
 *
 * 硬上限**不覆盖**已落定后的宽限（最坏 6s + 1.5s）—— 否则数据在第 5.5 秒才回来时，
 * 布局宽限会被砍成半秒，又变回原来那个误判。
 *
 * 改造前只有「从 tour 启动起算 1.5s」一条：冷启动的 Plan home 请求常常慢于它，
 * 有草稿 goal 的用户被误判成没有 —— tour 塌成「1 of 1 · Go Brief」。
 *
 * @param elapsedSinceStartMs tour 启动至今
 * @param elapsedSinceSettledMs Pending 段数据落定至今；`null` = 尚未落定
 */
export function shouldGiveUpParkedGoalProbe(
  elapsedSinceStartMs: number,
  elapsedSinceSettledMs: number | null,
): boolean {
  if (elapsedSinceSettledMs !== null) {
    return elapsedSinceSettledMs >= HANDOFF_READY_TIMEOUT_MS;
  }
  return elapsedSinceStartMs >= HANDOFF_SOURCE_SETTLE_CAP_MS;
}

/**
 * 锚点迟迟不就绪时，要不要跳过当前拍。
 *
 * - 最后一拍（firstBrief）不跳：跳了整个 tour 就没了，留着暗幕 + 气泡让用户自己
 *   Skip / Go Brief。
 * - **beats 未定之前也不跳**：塌成单拍是 `resolveHandoffBeats` 的职责，它会把 beats
 *   重建成 `[firstBrief]` 并复位到第 0 拍。抢在它前面 advance 会先闪一下「2 of 2」再被
 *   改写成「1 of 1」—— 2026-09-21 Android 真机实拍到的就是这一下。
 *
 * beats 定了之后仍然要跳：那时锚点消失是真的消失（用户把 goal 激活了，pending 卡被摘掉）。
 */
export function shouldSkipBeatOnAnchorTimeout(
  beatId: HandoffBeatId,
  beatsResolved: boolean,
): boolean {
  return beatId !== HANDOFF_BEAT_IDS.firstBrief && beatsResolved;
}
